import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const STATUSES = ['ordered', 'received', 'cancelled'];
const LIMIT = 15;

function fmtMoney(n) { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

const BLANK_ITEM = { product_id: '', product_name: '', qty: 1, price: '' };

export default function PurchaseOrders({ setTitle }) {
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [status, setStatus]   = useState('');
  const [loading, setLoading] = useState(false);

  const [modal, setModal]         = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);

  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts]   = useState([]);

  const [supp, setSupp]   = useState('');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState([{ ...BLANK_ITEM }]);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});

  useEffect(() => { setTitle('Purchase Orders'); }, [setTitle]);

  useEffect(() => {
    api.get('/suppliers/all').then(r => setSuppliers(r.data || [])).catch(() => {});
    api.get('/products/all').then(r => setProducts(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/purchases?status=${status}&page=${page}&limit=${LIMIT}`);
      setItems(data.data || []); setTotal(data.total || 0);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [status, page]);

  useEffect(() => { load(); }, [load]);

  const addLine = () => setLineItems(p => [...p, { ...BLANK_ITEM }]);
  const removeLine = (i) => setLineItems(p => p.filter((_, idx) => idx !== i));
  const setLine = (i, k, v) => setLineItems(p => p.map((it, idx) => {
    if (idx !== i) return it;
    const updated = { ...it, [k]: v };
    if (k === 'product_id') {
      const prod = products.find(x => String(x.id) === String(v));
      if (prod) { updated.product_name = prod.name; updated.price = prod.cost || prod.price; }
    }
    return updated;
  }));

  const lineTotal = lineItems.reduce((s, it) => s + (parseFloat(it.qty || 0) * parseFloat(it.price || 0)), 0);

  const openCreate = () => { setSupp(''); setNotes(''); setLineItems([{ ...BLANK_ITEM }]); setModal(true); };
  const openView   = async (id) => {
    try { const { data } = await api.get(`/purchases/${id}`); setViewModal(data); }
    catch { alert('Could not load purchase order'); }
  };
  const openEdit = (item) => {
    setEditData({ id: item.id, supplier_id: item.supplier_id, status: item.status, notes: item.notes || '' });
    setEditModal(true);
  };

  const handleCreate = async () => {
    if (!supp) return alert('Supplier is required');
    const validItems = lineItems.filter(it => it.product_id && it.qty > 0 && it.price !== '');
    if (validItems.length === 0) return alert('Add at least one product');
    setSaving(true);
    try {
      await api.post('/purchases', { supplier_id: supp, notes, items: validItems });
      setModal(false); load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error creating PO');
    } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await api.put(`/purchases/${editData.id}`, { supplier_id: editData.supplier_id, status: editData.status, notes: editData.notes });
      setEditModal(false); load();
      if (viewModal?.id === editData.id) {
        const { data } = await api.get(`/purchases/${editData.id}`); setViewModal(data);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Error updating PO');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, num) => {
    if (!window.confirm(`Delete PO ${num}?`)) return;
    try { await api.delete(`/purchases/${id}`); load(); if (viewModal?.id === id) setViewModal(null); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Purchase Orders</h1>
        <button className="btn btn-primary" onClick={openCreate}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New PO
        </button>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="tab-bar" style={{ padding: 0, border: 0, gap: 6 }}>
            <button className={`tab-btn ${status === '' ? 'active' : ''}`} onClick={() => { setStatus(''); setPage(1); }}>All</button>
            {STATUSES.map(s => (
              <button key={s} className={`tab-btn ${status === s ? 'active' : ''}`} onClick={() => { setStatus(s); setPage(1); }}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} purchase orders</span>
        </div>

        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>PO #</th>
                  <th>Supplier</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th className="text-right">Total</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={6} className="empty">No purchase orders found</td></tr>
                ) : items.map(item => (
                  <tr key={item.id}>
                    <td className="font-mono"><strong>{item.po_number}</strong></td>
                    <td>{item.supplier_name || '—'}</td>
                    <td><span className={`badge badge-${item.status}`}>{item.status}</span></td>
                    <td className="text-muted">{item.created_at ? item.created_at.slice(0, 10) : '—'}</td>
                    <td className="text-right"><strong>{fmtMoney(item.total)}</strong></td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon" title="View" onClick={() => openView(item.id)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
                        </button>
                        <button className="btn-icon" title="Edit / Receive" onClick={() => openEdit(item)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id, item.po_number)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* Create PO Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal modal-xl">
            <div className="modal-header">
              <h2>New Purchase Order</h2>
              <button className="modal-close" onClick={() => setModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label>Supplier *</label>
                  <select className="form-control" value={supp} onChange={e => setSupp(e.target.value)}>
                    <option value="">Select supplier…</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <input className="form-control" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes" />
                </div>
              </div>

              <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Items to Order</div>
              <div className="items-table">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>Product</th>
                      <th style={{ width: '15%' }}>Qty</th>
                      <th style={{ width: '20%' }}>Unit Cost</th>
                      <th style={{ width: '20%' }} className="text-right">Subtotal</th>
                      <th style={{ width: '5%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((it, i) => (
                      <tr key={i}>
                        <td>
                          <select className="form-control" value={it.product_id} onChange={e => setLine(i, 'product_id', e.target.value)}>
                            <option value="">Select product…</option>
                            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </td>
                        <td>
                          <input type="number" className="form-control" value={it.qty} onChange={e => setLine(i, 'qty', e.target.value)} min="1" />
                        </td>
                        <td>
                          <input type="number" className="form-control" value={it.price} onChange={e => setLine(i, 'price', e.target.value)} step="0.01" min="0" placeholder="0.00" />
                        </td>
                        <td className="text-right">{fmtMoney(parseFloat(it.qty || 0) * parseFloat(it.price || 0))}</td>
                        <td>
                          {lineItems.length > 1 && (
                            <button className="btn-icon danger" onClick={() => removeLine(i)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="items-total">Total: {fmtMoney(lineTotal)}</div>
              </div>
              <button className="btn btn-ghost btn-sm mt-10" onClick={addLine}>+ Add Item</button>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
                {saving ? 'Creating…' : 'Create Purchase Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit PO Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>Update Purchase Order</h2>
              <button className="modal-close" onClick={() => setEditModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Supplier</label>
                  <select className="form-control" value={editData.supplier_id} onChange={e => setEditData(p => ({ ...p, supplier_id: e.target.value }))}>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Status</label>
                  <select className="form-control" value={editData.status} onChange={e => setEditData(p => ({ ...p, status: e.target.value }))}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Notes</label>
                  <textarea className="form-control" value={editData.notes} onChange={e => setEditData(p => ({ ...p, notes: e.target.value }))} />
                </div>
              </div>
              {editData.status === 'received' && (
                <div className="success-msg mt-10">Setting status to "Received" will automatically increase product stock.</div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleUpdate} disabled={saving}>
                {saving ? 'Saving…' : 'Update PO'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View PO Modal */}
      {viewModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewModal(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2>PO {viewModal.po_number}</h2>
              <button className="modal-close" onClick={() => setViewModal(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label>PO #</label><div className="font-mono">{viewModal.po_number}</div>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <div><span className={`badge badge-${viewModal.status}`}>{viewModal.status}</span></div>
                </div>
                <div className="form-group">
                  <label>Supplier</label><div>{viewModal.supplier_name}</div>
                </div>
                <div className="form-group">
                  <label>Date</label><div>{viewModal.created_at ? viewModal.created_at.slice(0, 10) : '—'}</div>
                </div>
                {viewModal.notes && (
                  <div className="form-group full">
                    <label>Notes</label><div>{viewModal.notes}</div>
                  </div>
                )}
              </div>

              <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Items</div>
              <div className="items-table">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th className="text-right">Qty</th>
                      <th className="text-right">Unit Cost</th>
                      <th className="text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewModal.items || []).map((it, i) => (
                      <tr key={i}>
                        <td>{it.product_name}</td>
                        <td className="text-right">{it.qty}</td>
                        <td className="text-right">{fmtMoney(it.price)}</td>
                        <td className="text-right">{fmtMoney(parseFloat(it.qty) * parseFloat(it.price))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="items-total">Total: {fmtMoney(viewModal.total)}</div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setViewModal(null)}>Close</button>
              <button className="btn btn-primary" onClick={() => { setViewModal(null); openEdit(viewModal); }}>Edit / Receive</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
